<template>
  <v-autocomplete
    ref="autocomplete"
    v-model="internalValue"
    v-bind="$attrs"
    :label="label"
    :items="states"
    item-title="text"
    :multiple="multiple"
    :loading="isLoading"
    :custom-filter="filterHandler"
    clearable
    :menu-props="{ closeOnContentClick: true }"
    data-1p-ignore
  >
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps" :title="undefined">
        <template #prepend>
          <v-avatar
            size="30"

            min-width="30"
            rounded
            color="primary"
            class="mr-3"
          >
            {{ item.value }}
          </v-avatar>
        </template>
        <v-list-item-title>
          <strong>{{ item.title }}</strong>
        </v-list-item-title>
      </v-list-item>
    </template>
    <template v-if="multiple" #selection="{ item }">
      <CommonChip closable color="primary" @click:close="removeState(item.value)">
        {{ item.value }}
      </CommonChip>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { isArray } from 'lodash-es';

import { INameValue, ITextValue } from '@/types/common';
import { Nullable } from '@/utils/types';
import { addressService } from '@/bundles/Address/factory/addressServiceFactory';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';

interface IStateSelect {
  isLoading: boolean;
  states: ITextValue<string>[];
}

export default defineComponent({
  name: 'StateSelect',

  components: { CommonChip },

  props: {
    modelValue: {
      type: [Array, String] as PropType<Array<string> | Nullable<string>>,
      default: null,
    },
    label: {
      type: String,
      default: 'State / Province / Region'
    },
    country: {
      type: String,
      default: null,
    },
    multiple: Boolean,
    autoOpenMenu: Boolean,
  },

  emits: ['update:modelValue'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): IStateSelect => ({
    isLoading: false,
    states: [],
  }),

  computed: {
    internalValue: {
      get (): string[] | Nullable<string> {
        return this.modelValue;
      },
      set (value: string[] | Nullable<string>) {
        this.$emit('update:modelValue', value);
      }
    }
  },

  watch: {
    country () {
      this.fetchStates();
    }
  },

  created () {
    this.addModelValueToStates();
    this.fetchStates();
  },

  mounted () {
    if (this.autoOpenMenu) {
      (this.$refs.autocomplete as any).menu = true;
    }
  },

  methods: {
    removeState: function (value: string) {
      if (isArray(this.internalValue)) {
        this.internalValue = this.internalValue.filter((i) => i !== value);
        return;
      }

      this.internalValue = null;
    },
    filterHandler: function (value: string, queryText: string, item?: any) {
      const query = queryText.toLowerCase();
      const raw: ITextValue<string> | undefined = item?.raw;

      return value.toLowerCase().includes(query) || !!raw?.value?.toLowerCase().includes(query);
    },
    async fetchStates () {
      try {
        this.isLoading = true;

        const params: INameValue<string>[] = [];

        if (this.country) {
          params.push({ name: 'country', value: this.country });
        }

        this.states = await addressService.getStates(params);
        this.removeNotExistedModelValues();
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.isLoading = false;
      }
    },
    addModelValueToStates () {
      if (!this.internalValue) {
        return;
      }

      if (Array.isArray(this.internalValue)) {
        this.internalValue.forEach((value) => {
          if (!this.states.find((state) => state.value === value)) {
            this.states.push({ text: value, value });
          }
        });
        return;
      }

      if (!this.states.find((state) => state.value === this.internalValue)) {
        this.states.push({ text: this.internalValue, value: this.internalValue });
      }
    },
    removeNotExistedModelValues () {
      if (!this.internalValue) {
        return;
      }

      if (Array.isArray(this.internalValue)) {
        this.internalValue = this.internalValue.filter((value) => this.states.some((state) => state.value === value));
        return;
      }

      if (!this.states.some((state) => state.value === this.internalValue)) {
        this.internalValue = null;
      }
    }
  }
});
</script>
